import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Box, Link } from '@mui/material';
import { PATHS } from 'src/navigation';
import { getTenantAdminData, registerTenantUser } from 'src/redux/tenants/actions';
import { H1, TextLink, FormikInputString, FormikPassword, Button, Loader, P3, FormikInputPhone, SomethingWentWrongState } from 'src/components';
import {
  selectRegisterTenantUserErrors,
  selectRegisterTenantUserLoading,
  selectRegisterTenantUserSuccess,
  selectTenantAdmin,
  selectTenantAdminDataError,
  selectTenantAdminDataLoading,
} from 'src/redux/tenants/selectors';
import { themeColors } from 'src/theme';
import { VALIDATOR } from 'src/utils/validationSchemes';
import Blank from '../wrappers/Blank';

const TERMS_AND_CONDITIONS_LINK = 'https://storm360.com/user-agreement';

type FormValues = {
  firstName: string;
  lastName: string;
  phone: string;
  password: string;
  confirmPassword: string;
};

const CreateAccountTenantUserContainer: React.FC = (): JSX.Element => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { token } = useParams();
  const creatingUserErrors = useSelector(selectRegisterTenantUserErrors) as Record<string, string[]>;
  const creatingUserSuccess = useSelector(selectRegisterTenantUserSuccess);
  const creatingUserLoading = useSelector(selectRegisterTenantUserLoading);
  const loadingAdminData = useSelector(selectTenantAdminDataLoading);
  const loadAdminDataError = useSelector(selectTenantAdminDataError);
  const adminData = useSelector(selectTenantAdmin);

  useEffect(() => {
    if (creatingUserSuccess) {
      navigate(PATHS.MY_PROFILE_PAGE, { replace: true });
    }
  }, [dispatch, navigate, creatingUserSuccess]);

  const initialValues: FormValues = {
    firstName: adminData.firstName || '',
    lastName: adminData.lastName || '',
    phone: adminData.phone || '',
    password: '',
    confirmPassword: '',
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      firstName: VALIDATOR.name,
      lastName: VALIDATOR.name,
      phone: VALIDATOR.phone,
      password: VALIDATOR.password,
      confirmPassword: VALIDATOR.confirmPassword,
    }),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onSubmit: ({ confirmPassword, ...data }) => {
      dispatch(
        registerTenantUser.init({
          code: token,
          data: {
            ...data,
            firstName: data.firstName.trim(),
            lastName: data.lastName.trim(),
          },
        }),
      );
    },
  });

  const openTermsAndConditions = () => {
    window.open(TERMS_AND_CONDITIONS_LINK, '_blank', 'noreferrer');
  };

  useEffect(() => {
    dispatch(getTenantAdminData.init({ code: token }));
  }, []);

  if (loadingAdminData) {
    return (
      <Blank>
        <Loader />
      </Blank>
    );
  }

  if (loadAdminDataError) {
    return (
      <Blank>
        <SomethingWentWrongState />
      </Blank>
    );
  }

  return (
    <Blank>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ width: '280px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', my: '24px' }}>
            <H1 sx={{ textAlign: 'center' }}>Welcome to {adminData.tenant?.title || 'Storm360'}!</H1>
            <TextLink style={{ display: 'flex', alignItems: 'center' }}>To access your account, please Sign Up.</TextLink>
          </Box>
          <Box sx={{ mb: '24px' }}>
            <FormikInputString formik={formik} field="firstName" label="First Name" errorHelper={creatingUserErrors} />

            <FormikInputString formik={formik} field="lastName" label="Last Name" errorHelper={creatingUserErrors} />

            <FormikInputPhone formik={formik} field="phone" label={t('companyDashboardPage.phone')} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <FormikPassword
                formik={formik}
                field="password"
                label={t('createAccountPage.createPassword')}
                errorHelper={creatingUserErrors}
                autoComplete="new-password"
              />

              <FormikPassword
                formik={formik}
                field="confirmPassword"
                label={t('createAccountPage.repeatPassword')}
                errorHelper={creatingUserErrors}
                autoComplete="new-password"
              />
            </Box>
            <P3 sx={{ color: 'red' }}>{creatingUserErrors?.message}</P3>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <TextLink>
              {t('createAccountPage.agreeStatement')}
              <Link
                component="button"
                variant="body2"
                style={{
                  color: themeColors.blue,
                  textDecoration: 'none',
                  marginLeft: '4px',
                  fontSize: '14px',
                }}
                onClick={openTermsAndConditions}
              >
                {t('createAccountPage.termsAndConditions')}.
              </Link>
            </TextLink>

            <Button fullWidth loading={creatingUserLoading} onClick={() => formik.handleSubmit()} style={{ textTransform: 'none' }}>
              Sign Up
            </Button>
          </Box>
        </Box>
      </Box>
    </Blank>
  );
};

export default CreateAccountTenantUserContainer;
